import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  Copy,
  ExternalLink,
  ChevronDown,
  Loader2,
  Zap,
  Unlink,
  Ban,
} from "lucide-react";
import { toast } from "sonner";
import {
  listSupports,
  createSupports,
  assignSupport,
  releaseSupport,
  disableSupport,
  formatApiError,
} from "@/api";

const STATUS = {
  available: { label: "Disponible", cls: "bg-[#F5F4F0] text-[#78716C] border-[#E7E2D7]" },
  assigned: { label: "Asignado", cls: "bg-[#EDF7EE] text-[#2F7D3B] border-[#CBE5CF]" },
  disabled: { label: "Desactivado", cls: "bg-[#FBEFEA] text-[#A84C2A] border-[#F0D5C8]" },
};

const pillBtn =
  "flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border border-[#E7E2D7] text-[#57534E] hover:border-[#C25E38]/40 hover:text-[#C25E38] transition-colors duration-200 cursor-pointer bg-white/70 disabled:opacity-50 disabled:cursor-not-allowed";

const formatDate = (iso) =>
  iso
    ? new Intl.DateTimeFormat("es-ES", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(iso))
    : "—";

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  }
  toast.success("URL copiada al portapapeles.");
};

export const NfcSupports = ({ restaurants }) => {
  const [supports, setSupports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [assignTarget, setAssignTarget] = useState({});
  const [busy, setBusy] = useState(null);
  const [confirmDisable, setConfirmDisable] = useState(null);

  const load = () =>
    listSupports()
      .then(setSupports)
      .catch(() => toast.error("No se han podido cargar los soportes."))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (count) => {
    if (creating) return;
    setCreating(true);
    try {
      await createSupports(count);
      toast.success(count === 1 ? "Soporte creado." : "50 soportes creados.");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "No se han podido crear los soportes."));
    } finally {
      setCreating(false);
    }
  };

  const run = async (code, fn, okMsg) => {
    if (busy) return;
    setBusy(code);
    try {
      await fn();
      toast.success(okMsg);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "No se ha podido completar la acción."));
    } finally {
      setBusy(null);
      setConfirmDisable(null);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.22, ease: "easeOut" }}
      className="flex flex-col gap-4"
      data-testid="nfc-section"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif-display text-xl sm:text-2xl font-medium text-[#1C1917]">
          ⚡ Soportes NFC
        </h2>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="create-support-button"
            onClick={() => handleCreate(1)}
            disabled={creating}
            className={pillBtn}
          >
            {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
            Crear soporte
          </button>
          <button
            type="button"
            data-testid="create-50-supports-button"
            onClick={() => handleCreate(50)}
            disabled={creating}
            className={pillBtn}
          >
            <Zap className="h-3 w-3" /> Crear 50 soportes
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-[#A8A29E] font-mono-label uppercase tracking-[0.25em] animate-pulse">
          Cargando...
        </p>
      ) : supports.length === 0 ? (
        <div className="bg-white/60 border border-dashed border-[#E7E2D7] rounded-2xl p-10 text-center">
          <p className="text-sm text-[#A8A29E]">No hay soportes todavía.</p>
        </div>
      ) : (
        <ul className="flex flex-col gap-3" data-testid="supports-list">
          {supports.map((s) => {
            const st = STATUS[s.status] || STATUS.available;
            const url = `${window.location.origin}/t/${s.support_code}`;
            const isOpen = expanded === s.support_code;
            return (
              <li
                key={s.id}
                data-testid={`support-row-${s.support_code}`}
                className="bg-white border border-[#E7E2D7] rounded-2xl shadow-[0_10px_30px_-10px_rgba(28,25,23,0.06)]"
              >
                <button
                  type="button"
                  data-testid={`support-toggle-${s.support_code}`}
                  onClick={() => setExpanded(isOpen ? null : s.support_code)}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 cursor-pointer text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono-label text-sm font-medium text-[#1C1917]">
                      {s.support_code}
                    </span>
                    <span
                      data-testid={`support-status-${s.support_code}`}
                      className={`text-[10px] font-medium uppercase tracking-widest rounded-full px-2.5 py-1 border ${st.cls}`}
                    >
                      {st.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[#57534E] truncate max-w-[7rem] sm:max-w-none">
                      {s.restaurant_name || "—"}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-[#A8A29E] transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                    />
                  </div>
                </button>

                {isOpen && (
                  <div
                    className="border-t border-[#E7E2D7] px-5 py-4 flex flex-col gap-4"
                    data-testid={`support-detail-${s.support_code}`}
                  >
                    <div className="flex flex-col gap-1.5">
                      <p className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-[#A8A29E]">
                        URL NFC
                      </p>
                      <p
                        className="text-xs text-[#57534E] break-all font-mono-label bg-white/70 border border-[#E7E2D7] rounded-xl px-3.5 py-2.5"
                        data-testid={`support-url-${s.support_code}`}
                      >
                        {url}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-[#A8A29E]">Creado</p>
                        <p className="text-[#57534E] mt-1">{formatDate(s.created_at)}</p>
                      </div>
                      <div>
                        <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-[#A8A29E]">Última asignación</p>
                        <p className="text-[#57534E] mt-1" data-testid={`support-assigned-at-${s.support_code}`}>
                          {formatDate(s.assigned_at)}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        data-testid={`copy-support-url-${s.support_code}`}
                        onClick={() => copyText(url)}
                        className={pillBtn}
                      >
                        <Copy className="h-3 w-3" /> Copiar URL
                      </button>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`open-support-url-${s.support_code}`}
                        className={pillBtn}
                      >
                        <ExternalLink className="h-3 w-3" /> Abrir URL
                      </a>
                    </div>

                    {s.status !== "disabled" && (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <select
                          data-testid={`assign-select-${s.support_code}`}
                          value={assignTarget[s.support_code] || ""}
                          onChange={(e) =>
                            setAssignTarget((prev) => ({
                              ...prev,
                              [s.support_code]: e.target.value,
                            }))
                          }
                          className="flex-1 rounded-xl border border-[#E7E2D7] bg-white/90 px-3.5 py-2.5 text-sm text-[#1C1917] focus:outline-none focus:ring-2 focus:ring-[#C25E38]/30 cursor-pointer"
                        >
                          <option value="">Seleccionar restaurante...</option>
                          {restaurants.map((r) => (
                            <option key={r.id} value={r.slug}>
                              {r.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          data-testid={`assign-confirm-${s.support_code}`}
                          disabled={!assignTarget[s.support_code] || busy === s.support_code}
                          onClick={() =>
                            run(
                              s.support_code,
                              () => assignSupport(s.support_code, assignTarget[s.support_code]),
                              s.status === "assigned" ? "Restaurante cambiado." : "Soporte asignado.",
                            )
                          }
                          className="text-xs font-medium rounded-xl px-4 py-2.5 bg-[#1C1917] text-[#FDFBF7] hover:bg-[#C25E38] transition-colors duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {busy === s.support_code ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : s.status === "assigned" ? (
                            "Cambiar restaurante"
                          ) : (
                            "Asignar restaurante"
                          )}
                        </button>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {s.status === "assigned" && (
                        <button
                          type="button"
                          data-testid={`release-support-${s.support_code}`}
                          disabled={busy === s.support_code}
                          onClick={() =>
                            run(s.support_code, () => releaseSupport(s.support_code), "Soporte liberado.")
                          }
                          className={pillBtn}
                        >
                          <Unlink className="h-3 w-3" /> Liberar soporte
                        </button>
                      )}
                      {s.status !== "disabled" &&
                        (confirmDisable === s.support_code ? (
                          <>
                            <button
                              type="button"
                              data-testid={`confirm-disable-${s.support_code}`}
                              onClick={() =>
                                run(s.support_code, () => disableSupport(s.support_code), "Soporte desactivado.")
                              }
                              className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 bg-[#C25E38] text-[#FDFBF7] hover:bg-[#A84C2A] transition-colors duration-200 cursor-pointer"
                            >
                              <Ban className="h-3 w-3" /> Confirmar desactivación
                            </button>
                            <button
                              type="button"
                              data-testid={`cancel-disable-${s.support_code}`}
                              onClick={() => setConfirmDisable(null)}
                              className={pillBtn}
                            >
                              Cancelar
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            data-testid={`disable-support-${s.support_code}`}
                            onClick={() => setConfirmDisable(s.support_code)}
                            className={pillBtn}
                          >
                            <Ban className="h-3 w-3" /> Desactivar soporte
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </motion.section>
  );
};
